import {
  Stack,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { Controller } from "react-hook-form";  
import ReuseableLabel from "../ReusableComponents/ReuseableLabel.jsx";
import InfoBox from "../Common/InfoBox.jsx";
import { getMasterTooltipMessage } from "../../utils/helperFunctions.js";

const OtherSection = ({
  control,
  errors,
  watch,
  setValue,
  register,
  processVariableWatch,
  processVariables,
  expectDocumentWatch,
  documentCategories,
  documentSubCategories,
  isEditMode = false,
  defaultValues = {},
}) => {
  return (
    <Stack
      direction={"column"}
      gap={2}
      width={{
        md: "80%",
        lg: "80%",
        xl: "80%",
        sm: "100%",
        xs: "100%",
      }}
      margin={"0 auto"}
      mt={"20px"}
    >
      <Box>
        <Stack direction="column" spacing={1}>
          {/* Acknowledgement */}
          <TextField
            fullWidth
            variant="outlined"
            label="Acknowledgement"
            type="text"
            placeholder="Acknowledgement"
            size="small"
            {...register("Ack")}
            defaultValue={defaultValues.Ack || ""}
            error={Boolean(errors.Ack)}
            helperText={errors.Ack?.message}
          />

          {/* Process Variable */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            {processVariableWatch?.length > 0 && (
              <InfoBox
                message={getMasterTooltipMessage(
                  "processVariable",
                  "processVariable"
                )}
              />
            )}
            <Controller
              name="Process Variable"
              control={control}
              defaultValue={defaultValues["Process Variable"] || ""}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={processVariables || []}
                  getOptionLabel={(option) => option || ""}
                  disablePortal
                  fullWidth
                  size="small"
                  value={field.value || null}
                  onChange={(_, value) => field.onChange(value ?? "")}
                  isOptionEqualToValue={(option, value) => option === value}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Process Variable"
                      error={!!errors?.["Process Variable"]}
                      helperText={errors?.["Process Variable"]?.message}
                    />
                  )}
                />
              )}
            />
          </Box>

          {/* Scoring Engine */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  {...register("Scoring Engine")}
                  defaultChecked={defaultValues["Scoring Engine"] || false}
                />
              }
              label="Scoring Engine"
            />
          </Box>

          {/* Help Text */}
          <TextField
            fullWidth
            variant="outlined"
            type="text"
            placeholder="Help Text"
            label="Help Text"
            size="small"
            {...register("Help Text")}
            defaultValue={defaultValues["Help Text"] || ""}
            error={Boolean(errors?.["Help Text"])}
            helperText={errors?.["Help Text"]?.message}
          />

          {/* Document Related Fields */}
          <Stack direction="column" spacing={1}>
            {/* Expect Document */}
            <FormControl
              variant="outlined"
              style={{ marginTop: 1, minWidth: 150 }}
              size="small"
              error={Boolean(errors.expectDocument)}
            >
              <InputLabel id="demo-simple-select-required-label">
                Expect Document
              </InputLabel>
              <Controller
                name="expectDocument"
                control={control}
                defaultValue={defaultValues.expectDocument || ""}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      labelId="demo-simple-select-required-label"
                      id="demo-simple-select-required"
                      label="Expect Document"
                    >
                      <MenuItem value={""}>Please select an option</MenuItem>
                      <MenuItem value={"yes"}>Yes</MenuItem>
                      <MenuItem value={"no"}>No</MenuItem>
                    </Select>
                  </>
                )}
              />
            </FormControl>

            {/* Document Categories */}
            <FormControl
              variant="outlined"
              style={{ marginTop: 11, minWidth: 150 }}
              size="small"
              error={Boolean(errors.documentCategory)}
            >
              <InputLabel id="documentCategory">Document Categories</InputLabel>
              <Controller
                name="documentCategory"
                control={control}
                defaultValue={defaultValues.documentCategory || ""}
                render={({ field }) => (
                  <>
                    <Select
                      {...field}
                      labelId="documentCategory"
                      id="documentCategoryId"
                      label="Document Categories"
                      style={{ height: "43px" }}
                      error={Boolean(errors.documentCategory)}
                      disabled={expectDocumentWatch !== "yes" ? true : false}
                    >
                      {documentCategories.map((cate, idx) => {
                        return (
                          <MenuItem key={idx} value={cate.code}>
                            {cate.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {errors.documentCategory && (
                      <FormHelperText error>
                        {errors.documentCategory.message}
                      </FormHelperText>
                    )}
                  </>
                )}
              />
            </FormControl>

            {/* Document Sub Categories */}
            {documentSubCategories.length > 0 && (
              <FormControl
                variant="outlined"
                style={{ marginTop: 11, minWidth: 150 }}
                size="small"
                error={Boolean(errors.documentType)}
              >
                <InputLabel id="documentType">Document Type</InputLabel>
                <Controller
                  name="documentType"
                  control={control}
                  defaultValue={defaultValues.documentType || ""}
                  render={({ field }) => (
                    <>
                      <Select
                        {...field}
                        labelId="documentType"
                        id="documentType"
                        label="Document Type"
                        style={{ height: "43px" }}
                        error={Boolean(errors.documentType)}
                        disabled={expectDocumentWatch !== "yes" ? true : false}
                      >
                        {documentSubCategories.map((scate, idx) => {
                          return (
                            <MenuItem key={idx} value={scate.name}>
                              {scate.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                      {errors.documentType && (
                        <FormHelperText error>
                          {errors.documentType.message}
                        </FormHelperText>
                      )}
                    </>
                  )}
                />
              </FormControl>
            )}
          </Stack>

          {/* Range Start/End */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <InfoBox
              message={getMasterTooltipMessage("range", "range")}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "25px",
              }}
            >
              <TextField
                variant="outlined"
                type="number"
                label="Range Start"
                name="rangeStart"
                size="small"
                placeholder="Range Start"
                InputProps={{
                  sx: { borderRadius: "6px", width: "100%" },
                }}
                {...register("rangeStart")}
                defaultValue={defaultValues.rangeStart || ""}
                error={Boolean(errors.rangeStart)}
                helperText={errors.rangeStart?.message}
              />
              <TextField
                variant="outlined"
                type="number"
                label="Range End"
                name="rangeEnd"
                placeholder="Range End"
                size="small"
                InputProps={{
                  sx: { borderRadius: "6px", width: "100%" },
                }}
                {...register("rangeEnd")}
                defaultValue={defaultValues.rangeEnd || ""}
                error={Boolean(errors.rangeEnd)}
                helperText={errors.rangeEnd?.message}
              />
            </Box>
          </Box>
        </Stack>
      </Box>
    </Stack>
  );
};

export default OtherSection;